import { Stack, styled, Typography } from '@mui/material';
import { format } from 'date-fns';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { CustomerJobStatus } from 'api/domain/entities/CustomerJob';

import { CustomerJob } from 'domain/entities/CustomerJob';
import { Entity } from 'domain/types/Entity';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { Button, PreviewBox, Tooltip } from 'targets/web/components';
import { componentShadows } from 'targets/web/theme/shadows';
import { Airplane } from 'targets/web/components/Icons/Airplane';

import { SimpleJobStatusChip } from './SimpleJobStatusChip';

const Container = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(6),
  minWidth: 260,
  width: 'inherit',
  maxWidth: 375,
  minHeight: 310,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  boxShadow: componentShadows.card,
}));

type CustomerJobLogCardProps = {
  job: Entity<CustomerJob>;
  onViewDetailsClick?: () => void;
  onCancelClick?: () => void;
};

export const CustomerJobLogCard = ({
  job,
  onViewDetailsClick,
  onCancelClick,
}: CustomerJobLogCardProps) => {
  const t = useTranslationPrefix('components.cards.customer_job_card');

  const serviceNames = job.requestedServices.map((s) => s.customerName).join(', ');

  return (
    <Container
      gap={4}
      onClick={onViewDetailsClick}
      sx={{ cursor: 'pointer' }}
      data-testname="customerJobCard"
    >
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" gap={3} alignItems="center">
            <Airplane />
            <Typography data-testname={`job_${job.id}`} variant="h5">
              {job.name}
            </Typography>
          </Stack>
          <ChevronRight />
        </Stack>

        <Typography>{job.station.name}</Typography>

        <SimpleJobStatusChip status={job.status} />
      </Stack>

      <Stack gap={2} flex={1}>
        <PreviewBox
          size="regular"
          direction="row"
          width="auto"
          justifyContent="start"
          alignItems="flex-start"
        >
          <Stack flex={1} gap={2}>
            {job.type === 'job' && job.estimatedCompletionTime && (
              <Stack alignSelf="start" gap={1}>
                <Typography variant="inputLabel">{t('completion_date')}</Typography>
                <Typography variant="labelSmall">
                  {format(job.estimatedCompletionTime, 'MMM dd, yyyy')}
                </Typography>
              </Stack>
            )}

            <Stack alignSelf="start" gap={1}>
              <Typography variant="inputLabel">{t('services_list')}</Typography>

              {job.requestedServices.length > 5 ? (
                <Tooltip title={serviceNames}>
                  <Typography variant="labelSmall">
                    {t('more_services', { count: job.requestedServices.length })}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="labelSmall">{serviceNames}</Typography>
              )}
            </Stack>
          </Stack>
        </PreviewBox>
      </Stack>

      {job.type === 'request' && job.status === CustomerJobStatus.Values.submitted && (
        <Button
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onCancelClick?.();
          }}
        >
          Cancel
        </Button>
      )}
    </Container>
  );
};
